import React, { useEffect, useState } from "react";
import { Tag, Button, message, Spin, Alert } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  CameraOutlined,
  CopyOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  WarningOutlined,
  RightOutlined,
  RobotOutlined,
} from "@ant-design/icons";
import PageHeader from "../../components/generic/PageHeader";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getResultSummaryByKey,
  getUITestSteps,
  getTestResultLogFail,
} from "../../service/result.service";
import "../../styles/db.css";
import { StatsStrip } from "../../components/results/ResultStats";

const ACTION_COLOR = {
  Navigation: "#1677ff",
  Interaction: "#722ed1",
  Waiting: "#fa8c16",
  Assert: "#13c2c2",
  Screenshot: "#eb2f96",
  JavaScript: "#faad14",
  Frames: "#52c41a",
  Variables: "#2f54eb",
};

function ActionTag({ action, category }) {
  const color = ACTION_COLOR[category] || "#666";
  return (
    <span
      style={{
        display: "inline-block",
        background: color + "18",
        color,
        border: `1px solid ${color}40`,
        borderRadius: 4,
        padding: "1px 8px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        fontFamily: "monospace",
        whiteSpace: "nowrap",
      }}
    >
      {action}
    </span>
  );
}

function parseStepName(testName = "") {
  const dotIdx = testName.indexOf(". ");
  const seq = dotIdx > -1 ? parseInt(testName.substring(0, dotIdx), 10) : 0;
  const rest = dotIdx > -1 ? testName.substring(dotIdx + 2) : testName;
  const dashIdx = rest.indexOf(" — ");
  const action = dashIdx > -1 ? rest.substring(0, dashIdx) : rest;
  const desc = dashIdx > -1 ? rest.substring(dashIdx + 3) : "";
  return { seq, action, desc };
}

function resolveStatus(step) {
  const raw = (step.testStatus || step.testResults || "").trim();
  if (/^pass/i.test(raw)) return "Pass";
  if (/^fail/i.test(raw)) return "Fail";
  if (/^skip/i.test(raw)) return "Skip";
  if (step.passCount > 0) return "Pass";
  if (step.failCount > 0) return "Fail";
  if (step.totalPassCount > 0) return "Pass";
  if (step.totalFailCount > 0) return "Fail";
  return "Skip";
}

function StepRow({ step, failDetail }) {
  const [expanded, setExpanded] = useState(false);
  const status = resolveStatus(step);
  const isFail = status === "Fail";
  const isPass = status === "Pass";
  const { seq, action, desc } = parseStepName(step.testName);
  const category = step.testType || "";
  const circleColor = isPass ? "#52c41a" : isFail ? "#ff4d4f" : "#bbb";

  const copyPath = (path) => {
    navigator.clipboard
      .writeText(path)
      .then(() => message.success("Path copied"))
      .catch(() => message.error("Could not copy"));
  };

  return (
    <div
      style={{
        borderBottom: "1px solid #f0f0f0",
        background: isFail ? "#fff9f9" : "transparent",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "10px 16px",
          gap: 12,
          cursor: isFail ? "pointer" : "default",
        }}
        onClick={() => isFail && setExpanded(!expanded)}
      >
        <span
          style={{
            minWidth: 28,
            height: 28,
            borderRadius: "50%",
            background: circleColor,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          {seq || "?"}
        </span>
        <span style={{ flexShrink: 0 }}>
          {isPass ? (
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />
          ) : isFail ? (
            <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
          ) : (
            <MinusCircleOutlined style={{ color: "#bbb", fontSize: 16 }} />
          )}
        </span>
        <ActionTag action={action} category={category} />
        <span style={{ flex: 1, fontSize: 13, color: "#444" }}>
          {desc || "—"}
        </span>
        {category && (
          <span
            style={{
              fontSize: 11,
              color: "#999",
              minWidth: 90,
              textAlign: "right",
            }}
          >
            {category}
          </span>
        )}
        {isFail && (
          <RightOutlined
            style={{
              fontSize: 10,
              color: "#ff4d4f",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        )}
      </div>
      {isFail && expanded && (
        <div
          style={{
            margin: "0 16px 12px 56px",
            padding: "12px 16px",
            background: "#fff2f0",
            border: "1px solid #ffccc7",
            borderRadius: 6,
          }}
        >
          {failDetail ? (
            <>
              {failDetail.rawData && (
                <div style={{ marginBottom: 10 }}>
                  <WarningOutlined
                    style={{ color: "#cf1322", marginRight: 6 }}
                  />
                  <span style={{ fontWeight: 600, color: "#cf1322" }}>
                    Error:{" "}
                  </span>
                  <span style={{ fontSize: 13, color: "#333" }}>
                    {failDetail.rawData}
                  </span>
                </div>
              )}
              {failDetail.expectedValue && failDetail.expectedValue !== "" && (
                <div style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
                  <span style={{ fontWeight: 600 }}>Locator: </span>
                  <code
                    style={{
                      background: "#f5f5f5",
                      padding: "1px 6px",
                      borderRadius: 3,
                      wordBreak: "break-all",
                    }}
                  >
                    {failDetail.expectedValue}
                  </code>
                </div>
              )}
              {failDetail.columnName && failDetail.columnName !== "" && (
                <div style={{ marginBottom: 8, fontSize: 12, color: "#666" }}>
                  <span style={{ fontWeight: 600 }}>Value: </span>
                  <code
                    style={{
                      background: "#f5f5f5",
                      padding: "1px 6px",
                      borderRadius: 3,
                    }}
                  >
                    {failDetail.columnName}
                  </code>
                </div>
              )}
              {failDetail.actualValue &&
              failDetail.actualValue.trim() !== "" ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#fff",
                    border: "1px solid #ffccc7",
                    borderRadius: 4,
                    padding: "6px 10px",
                    marginTop: 8,
                  }}
                >
                  <CameraOutlined style={{ color: "#888", flexShrink: 0 }} />
                  <code
                    style={{
                      fontSize: 11,
                      color: "#555",
                      flex: 1,
                      wordBreak: "break-all",
                    }}
                  >
                    {failDetail.actualValue}
                  </code>
                  <Button
                    size="small"
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={() => copyPath(failDetail.actualValue)}
                  >
                    Copy path
                  </Button>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: "#bbb", marginTop: 8 }}>
                  <CameraOutlined style={{ marginRight: 4 }} />
                  No screenshot — configure Screenshot Folder in suite settings
                </div>
              )}
            </>
          ) : (
            <div style={{ color: "#bbb", fontSize: 12 }}>
              No failure detail recorded.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function UITestResultDetail() {
  const location = useLocation();
  const navigate = useNavigate();

  const resultSummaryKey =
    location.state?.resultSummaryKey ||
    location.state?.key ||
    new URLSearchParams(location.search).get("key");
  const passedRecord = location.state?.record;

  const [summary, setSummary] = useState(null);
  const [steps, setSteps] = useState([]);
  const [failDetails, setFailDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!resultSummaryKey) {
      navigate("/results");
      return;
    }

    getResultSummaryByKey(resultSummaryKey)
      .then((res) => setSummary(res.data))
      .catch(() => message.error("Failed to load result header"));

    getUITestSteps(resultSummaryKey)
      .then((res) => {
        const data = res.data || [];
        data.sort(
          (a, b) =>
            parseStepName(a.testName).seq - parseStepName(b.testName).seq,
        );
        setSteps(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        message.error("Failed to load steps");
      });

    getTestResultLogFail(resultSummaryKey, "Fail")
      .then((res) => setFailDetails(res.data || []))
      .catch(() => {});
  }, [resultSummaryKey]);

  const passed = steps.filter((s) => resolveStatus(s) === "Pass").length;
  const failed = steps.filter((s) => resolveStatus(s) === "Fail").length;
  const skipped = steps.filter((s) => resolveStatus(s) === "Skip").length;
  const total = steps.length;
  const passRate = total > 0 ? Math.round((passed / total) * 100) : 0;

  const overallStatus = summary?.testResult || passedRecord?.testResult || "—";
  const suiteName =
    summary?.suiteName ||
    summary?.inputName ||
    passedRecord?.suiteName ||
    "UI Test";
  const runBy = summary?.user || passedRecord?.user || "—";
  const runStart = summary?.runStart || passedRecord?.runStart || "—";
  const runEnd = summary?.runEnd || passedRecord?.runEnd;

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: "16px 24px" }}>
      {/* ── Header ──────────────────────────────────────── */}
      <PageHeader
        backTo={-1}
        icon={<RobotOutlined />}
        title={suiteName}
        subtitle={
          <div
            style={{ display: "flex", gap: 20, flexWrap: "wrap" }}
          >
            <span style={{ fontSize: 12, color: "#888" }}>
              <UserOutlined style={{ marginRight: 4 }} />
              {runBy}
            </span>
            <span style={{ fontSize: 12, color: "#888" }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              Started {runStart}
            </span>
            {runEnd && runEnd !== runStart && (
              <span style={{ fontSize: 12, color: "#888" }}>
                <ClockCircleOutlined style={{ marginRight: 4 }} />
                Ended {runEnd}
              </span>
            )}
            <span style={{ fontSize: 12, color: "#888" }}>
              <span style={{ fontWeight: 500 }}>Job: </span>
              {resultSummaryKey}
            </span>
          </div>
        }
        actions={
          <Tag
            color={
              overallStatus === "Pass"
                ? "success"
                : overallStatus === "Fail"
                  ? "error"
                  : "default"
            }
            style={{ fontSize: 13, padding: "3px 12px", fontWeight: 600 }}
          >
            {overallStatus === "Pass"
              ? "✓ Passed"
              : overallStatus === "Fail"
                ? "✗ Failed"
                : overallStatus}
          </Tag>
        }
      />

      {/* ── Stats strip ────────────────────────────────────── */}
      <StatsStrip
        items={[
          { label: "Total Steps", value: total, color: "#93C5FD" },
          { label: "Passed", value: passed, color: "#86EFAC" },
          { label: "Failed", value: failed, color: failed > 0 ? "#FCA5A5" : "rgba(255,255,255,0.3)" },
          { label: "Skipped", value: skipped, color: skipped > 0 ? "#FCD34D" : "rgba(255,255,255,0.3)" },
        ]}
        segments={[
          { value: passed,  color: "#86EFAC" },
          { value: failed,  color: "#FCA5A5" },
          { value: skipped, color: "rgba(255,255,255,0.35)" },
        ]}
      />

      {/* ── Failure alert ─────────────────────────────── */}
      {failed > 0 && (
        <Alert
          type="error"
          showIcon
          message={`${failed} step${failed > 1 ? "s" : ""} failed`}
          description="Click a failed step below to see the error, locator used, and screenshot path."
          style={{ marginBottom: 20, borderRadius: 8 }}
          closable
        />
      )}

      {/* ── Step list ─────────────────────────────────── */}
      <div
        style={{
          background: "#fff",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 16px",
            background: "#fafafa",
            borderBottom: "1px solid #f0f0f0",
            gap: 12,
            fontSize: 11,
            fontWeight: 600,
            color: "#999",
            textTransform: "uppercase",
            letterSpacing: 0.5,
          }}
        >
          <span style={{ minWidth: 28 }}>#</span>
          <span style={{ minWidth: 16 }} />
          <span style={{ minWidth: 100 }}>Action</span>
          <span style={{ flex: 1 }}>Description</span>
          <span style={{ minWidth: 90, textAlign: "right" }}>Category</span>
          <span style={{ minWidth: 14 }} />
        </div>
        {steps.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#bbb" }}>
            No step results recorded for this run.
          </div>
        ) : (
          steps.map((step, idx) => {
            const { seq } = parseStepName(step.testName);
            const detail = failDetails.find(
              (d) => String(d.row) === String(seq),
            );
            return (
              <StepRow
                key={step.key || step.resultLogId || idx}
                step={step}
                failDetail={detail || null}
              />
            );
          })
        )}
      </div>

      {/* ── Footer ────────────────────────────────────── */}
      <div
        style={{
          marginTop: 16,
          padding: "12px 0",
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
          color: "#999",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        <span>
          {total} steps · {passed} passed · {failed} failed · {skipped} skipped
        </span>
        <span>
          {runStart} – {runEnd || "—"}
        </span>
      </div>
    </div>
  );
}

export default UITestResultDetail;
